import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getVisit } from '../../utils/api/visits';
import { getModelForm } from '../../utils/api/modelForm';
import CustomFieldsForm from '../../componentsV2/FormInputs/CustomFieldsForm';
import DistributedItems from '../../componentsV2/Visits/DistributedItems';
import { formatNumericDate } from '../../utils/dates';
import { CurrentUserProvider } from '../../providers/CurrentUserProvider';
import { withContextProvider } from '../../hoc';
import { DialogsProvider, ToastProvider } from '../../providers';
import { getDistributedItemsProducts } from '../../utils/api/distributedItemsProducts';
import VisitButtonsFunctionality from '../../componentsV2/Visit/VisitButtonsFunctionality';
import HeaderWithBreadcrumbs from '../../componentsV2/Headers/HeaderWithBreadcrumbs';
import Icon from '../../componentsV2/Icons/Icon';
import { getRoleChecks } from '../../utils/roles';
import HeaderTitleRow from '../../componentsV2/Headers/HeaderTitleRow';
import { convertUrlsToLinks } from '../../utils/urlFinder';

// Helper function for Visit status
const visitComplete = (completed) => {
  if (completed === null || completed === undefined) {
    return 'N/A'; // Handle case where status is missing
  }
  return completed;
};

const VisitDetailPage = ({
  visitId, organizationId,
  currentUserRole,
}) => {
  const { isManager } = getRoleChecks(currentUserRole);
  const [visitData, setVisitData] = useState({});
  const [distributedItemProducts, setDistributedItemProducts] = useState([]);

  const visitFields = [
    { label: 'User', value: visitData.creator },
    { label: 'Date', value: visitData.date ? formatNumericDate(visitData.date) : null },
    { label: 'Purpose', value: visitData.purpose },
    { label: 'Leader', value: visitData.leader },
    { label: 'Visitors', value: visitData.visitors },
    { label: 'Workers Reached', value: visitData.workers_interacted },
    { label: 'Total Workers', value: visitData.total_workers },
    { label: 'Status', value: visitData.completed !== undefined ? visitComplete(visitData.completed) : null },
  ];

  useEffect(() => {
    const defaultData = {
      id: null,
      site_id: null,
      site_name: '',
      date: '',
      purpose: '',
      user_id: '',
      creator: '',
      leader: '',
      completed: '',
      visitors: '',
      provided: '',
      total_workers: '',
      workers_interacted: '',
      needs_concerns: '',
      other_notes: '',
      distributed_items: [],
      created_at: '',
      updated_at: '',
      routes_id: null,
      custom_fields: [],
    };

    getDistributedItemsProducts(organizationId)
      .then(distributedItemsData => {
        setDistributedItemProducts(distributedItemsData.products);
        // We check visitID to update inputs otherwise is a new visit
        if (visitId) {
          getVisit(organizationId, visitId)
            .then(async (visitDataResp) => {
              let customFields = visitDataResp.visit.custom_fields.map((item, index) => ({ ...item, position: index + 1, form_item_id: item.key }));
              // This in case that are no values for custom fields and are saved like an empty array.
              // If it's the case we need to pull the model form info doing another api
              if (customFields.length === 0) {
                customFields = await getModelForm('visit', organizationId)
                  .then((customFieldsData) => {
                    const items = customFieldsData?.form_items;
                    if (!Array.isArray(items)) {
                      return [];
                    }
                    return items.map((item, index) => ({
                      ...item,
                      position: index + 1,
                      form_item_id: item.key,
                    }));
                  });
              }
              const distributedItems = visitDataResp.visit.distributed_items?.map(item => {
                const categoryName = distributedItemsData.products.find(product => product.name === item.product_name)?.name;
                return { ...item, category: categoryName };
              });
              const completedFormated = visitDataResp.visit.completed === true ? 'Completed' : 'Attempted';
              const visit = {
                ...visitDataResp.visit,
                site_name: visitDataResp.visit.site_name,
                custom_fields: customFields,
                distributed_items: distributedItems,
                completed: completedFormated,
              };
              setVisitData(visit);
            });
        } else {
          getModelForm('visit', organizationId)
            .then((data) => {
              const customFields = data.form_items.map((item, index) => ({ ...item, position: index + 1, form_item_id: item.key }));
              setVisitData({ ...defaultData, custom_fields: customFields });
            });
        }
      });
  }, [organizationId, visitId]);

  if (!visitData.id) return null;

  const breadcrumbLinks = [
    { href: '/visits', name: 'Visits' },
    { href: `/sites/${visitData.site_id}`, name: visitData.site_name || 'Site name' },
    { href: `/visits/${visitId}`, name: visitData.purpose || 'Visit Details' },
  ];

  return (
    <CurrentUserProvider values={{ isManager }}>
      <DialogsProvider>
        <div className="w-[80%] ml-2.75">
          {/* Header */}
          <header>
            <div className="flex justify-between items-start w-full">
              {/* Breadcrumbs and Title */}
              <HeaderWithBreadcrumbs links={breadcrumbLinks} name="visits" label="Visits" size="h-9" shouldNotDisplayHeader />
            </div>
          </header>
          <div className="w-full">
            {/* Visit purpose and Buttons Section */}
            <HeaderTitleRow
              icon={<Icon dataTestId="visits-page-icon" name="visits" className="h-9" />}
              title={`${visitData.purpose} @ ${visitData.site_name}`}
              actions={(
                <VisitButtonsFunctionality
                  siteId={visitData.site_id}
                  visitId={visitId}
                  organizationId={organizationId}
                  visitDate={visitData.date}
                  siteName={visitData.site_name}
                  isManager={isManager}
                />
              )}
            />
            {/* Visit Info Section */}
            <section className="space-y-2">
              <div className="grid grid-cols-[auto,1fr] gap-x-16 gap-y-3">
                {visitFields.map(({ label, value }) => (
                  <React.Fragment key={label}>
                    <div className="text-base font-bold leading-6 text-vamos-black">{label}</div>
                    <div>{value !== undefined && value !== null ? value : 'N/A'}</div>
                  </React.Fragment>
                ))}
              </div>
            </section>

            {/* Visit Summary Section */}
            <section className="visit-summary mb-6 mt-24.5">
              <h3 className="border-b border-vamos-blue-gray pb-2 font-bold text-vamos-black text-xl">Visit Summary</h3>
              <p className="mt-4" data-testid="visit-summary">{convertUrlsToLinks(visitData.other_notes)}</p>
            </section>

            {/* Needs/Concerns Section */}
            <section className="needs-concerns mb-6 mt-24.5">
              <h3 className="border-b border-vamos-blue-gray pb-2 font-bold text-vamos-black text-xl">Needs/Concerns</h3>
              <p className="mt-4">{visitData.needs_concerns}</p>
            </section>

            {/* Custom Fields Section */}
            <section className="custom-fields mb-10 mt-10">
              <CustomFieldsForm
                customFields={(visitData.custom_fields || []).map((field) => ({
                  ...field,
                  label: field.label,
                }))}
                isLabelBeside={false}
                isViewOnly
              />
            </section>

            {/* Distributed Items Section */}
            <section className="distributed-items mt-21.5 mb-109">
              <DistributedItems
                isEdit={false}
                distItems={visitData.distributed_items}
                options={distributedItemProducts}
              />
            </section>
          </div>
        </div>
      </DialogsProvider>
    </CurrentUserProvider>
  );
};

VisitDetailPage.propTypes = {
  visitId: PropTypes.number.isRequired,
  organizationId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(DialogsProvider)(withContextProvider(ToastProvider)(VisitDetailPage));
